import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Button, 
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  DataTable,
  toast,
  Checkbox,
  Switch,
  Select,
} from '@etherisc/ui-kit';
import { usePermissions } from '../../hooks/usePermissions';
import {
  ChevronLeftIcon,
  FileTextIcon,
  CalendarIcon,
  AlertTriangleIcon,
  DollarSignIcon,
  PlayIcon,
  CheckIcon,
  XIcon,
  DownloadIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from 'lucide-react';
import { PositiveButton, NeutralButton } from '../../components/buttons';
import { apiClient } from '../../services/api';

interface ClaimsAssessment {
  id: string;
  participantId: string;
  runNumber: number;
  peril: string;
  payoutAmount: number;
  reading: number | null;
  eventDate: string | null;
  reviewDate: string;
  state: 'CALCULATED' | 'APPROVED' | 'PAID_OUT';
  mock: boolean;
  reportDetail?: 'minimal' | 'summary' | 'full' | null;
  reportData?: Record<string, unknown> | null;
  rawResponse?: Record<string, unknown> | null;
  createdAt: string;
}

interface ParticipantWithClaims {
  id: string;
  farmerId: string | null;
  farmer: {
    id: string;
    name: string;
    farmerCode: string;
    latitude: number | null;
    longitude: number | null;
    paymentDetails?: unknown;
  } | null;
  sumInsured: number | null;
  role: string;
  claimsAssessments: ClaimsAssessment[];
  latestAssessments: ClaimsAssessment[];
  totalPayout: number;
}

interface QuoteDetails {
  id: string;
  quoteNumber: string;
  state: string;
  coverageStartDate: string;
  coverageEndDate: string;
  sumInsured: number;
  product: {
    id: string;
    name: string;
    code: string;
  };
  organization: {
    id: string;
    name: string;
    code: string;
  };
  perilsConfig: {
    data?: {
      perils?: Array<{ peril_id: string; weight: number }>;
    };
    perils?: Array<{ peril_id: string; weight: number }>;
  };
  protectionLevels: Record<string, unknown> | null;
}

interface ClaimsDetailData {
  quote: QuoteDetails;
  participants: ParticipantWithClaims[];
}

export function ClaimsDetail() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  
  const [data, setData] = useState<ClaimsDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Check Claims Modal
  const [showCheckClaimsModal, setShowCheckClaimsModal] = useState(false);
  const [assessmentMode, setAssessmentMode] = useState<'production' | 'mock' | 'test'>('production');
  const [reportDetail, setReportDetail] = useState<'minimal' | 'summary' | 'full'>('summary');
  const [includeRawData, setIncludeRawData] = useState(false);
  const [assessing, setAssessing] = useState(false);
  const [testTriggers, setTestTriggers] = useState<Record<string, false | 'strike' | 'exhaust'>>({});
  const [dataCoveragePeriod, setDataCoveragePeriod] = useState<'whole' | 'germination' | 'part' | 'none'>('whole');
  
  // Selection for CALCULATED claims (approval auto-queues for payment)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approving, setApproving] = useState(false);
  
  // Job polling
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  
  // Expanded rows for summary display
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async () => {
    if (!quoteId) return;
    
    try {
      setLoading(true);
      const result = await apiClient.getClaimsForQuote(quoteId);
      // Transform the API response to match our component's expected shape
      const transformedData: ClaimsDetailData = {
        quote: result.quote as unknown as QuoteDetails,
        participants: result.participants as unknown as ParticipantWithClaims[],
      };
      setData(transformedData);
      setError(null);
    } catch (err) {
      console.error('Error fetching claims data:', err);
      setError('Failed to load claims data');
      toast.error('Failed to load claims data');
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const statusResult = await apiClient.getClaimsJobStatus(jobId);
      setJobStatus(statusResult.status);
      
      if (statusResult.status === 'completed') {
        // Get results and update database
        const resultsData = await apiClient.getClaimsJobResults(jobId);
        toast.success(resultsData.message || 'Claims assessment completed');
        setCurrentJobId(null);
        setJobStatus(null);
        setAssessing(false);
        fetchData(); // Refresh data
      } else if (statusResult.status === 'error' || statusResult.status === 'failed') {
        toast.error(`Claims assessment failed: ${statusResult.errorDetails || 'Unknown error'}`);
        setCurrentJobId(null);
        setJobStatus(null);
        setAssessing(false);
      } else {
        // Still processing, poll again in 5 seconds
        setTimeout(() => pollJobStatus(jobId), 5000);
      }
    } catch (err) {
      console.error('[ClaimsDetail] Error polling job status:', err);
      // Continue polling on transient errors
      setTimeout(() => pollJobStatus(jobId), 5000);
    }
  }, [fetchData]);

  const handleCheckClaims = async () => {
    if (!quoteId) {
      console.log('[ClaimsDetail] handleCheckClaims: No quoteId');
      return;
    }
    
    console.log('[ClaimsDetail] handleCheckClaims: Starting assessment', JSON.stringify({ quoteId, assessmentMode }));
    
    try {
      setAssessing(true);

      const assessParams: Parameters<typeof apiClient.triggerClaimsAssessment>[1] = {
        mock: assessmentMode === 'mock',
        mockValue: 100,
        reportDetail,
        includeRawData: includeRawData && reportDetail === 'full',
      };

      if (assessmentMode === 'test') {
        const activePerils = data?.quote?.perilsConfig?.data?.perils ?? data?.quote?.perilsConfig?.perils ?? [];
        const triggers: Record<string, false | string> = {
          data_coverage_period: dataCoveragePeriod,
        };
        for (const p of activePerils) {
          const key = `${p.peril_id.toLowerCase()}_trigger`;
          triggers[key] = testTriggers[p.peril_id] || false;
        }
        assessParams.testMode = true;
        assessParams.testTriggers = triggers;
      }

      const result = await apiClient.triggerClaimsAssessment(quoteId, assessParams);
      console.log('[ClaimsDetail] handleCheckClaims: Result', JSON.stringify(result, null, 2));
      setShowCheckClaimsModal(false);
      
      if (result.jobId) {
        // Async mode - poll for results
        toast.info(`Claims assessment job started. Estimated time: ${result.estimatedDuration || 60} seconds`);
        setCurrentJobId(result.jobId);
        setJobStatus('queued');
        // Start polling
        setTimeout(() => pollJobStatus(result.jobId!), 3000);
      } else {
        // Sync mode (including mock) - results are immediate
        toast.success(result.message || 'Claims assessment completed');
        setAssessing(false);
        fetchData(); // Refresh data
      }
    } catch (err) {
      console.error('[ClaimsDetail] Error assessing claims:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to assess claims');
      setAssessing(false);
    }
  };

  const handleApprove = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      setApproving(true);
      const result = await apiClient.bulkApproveClaims(Array.from(selectedIds));
      toast.success(`Approved ${result.approvedCount} claims`);
      setShowApproveModal(false);
      setSelectedIds(new Set());
      fetchData(); // Refresh data
    } catch (err) {
      console.error('Error approving claims:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to approve claims');
    } finally {
      setApproving(false);
    }
  };

  const toggleSelection = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const selectAllCalculated = () => {
    if (!data) return;
    const ids = data.participants
      .flatMap(p => p.latestAssessments)
      .filter(a => a.state === 'CALCULATED' && Number(a.payoutAmount) > 0)
      .map(a => a.id);
    setSelectedIds(new Set(ids));
  };

  const getStateColor = (state: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (state) {
      case 'CALCULATED': return 'secondary';
      case 'APPROVED': return 'default';
      case 'PAID_OUT': return 'outline';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <div className="alert alert-error">
          <AlertTriangleIcon size={20} />
          <span>{error || 'Failed to load data'}</span>
        </div>
      </div>
    );
  }

  const { quote, participants } = data;

  // Flatten assessments for the table
  const assessmentRows = participants.flatMap(p => 
    p.latestAssessments.map(a => ({
      ...a,
      farmerName: p.farmer?.name || 'Unknown',
      farmerCode: p.farmer?.farmerCode || '-',
      sumInsured: p.sumInsured,
      latitude: p.farmer?.latitude,
      longitude: p.farmer?.longitude,
    }))
  );

  // Only CALCULATED claims with payout > 0 are selectable (approval auto-queues for payment)
  const selectableCount = assessmentRows.filter(r => r.state === 'CALCULATED' && Number(r.payoutAmount) > 0).length;
  const allCurrentModeSelected = selectableCount > 0 && selectedIds.size === selectableCount;
  
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const participantColumns: any[] = [
    {
      id: 'select',
      header: () => (
        <Checkbox
          checked={allCurrentModeSelected}
          onCheckedChange={() => {
            if (selectedIds.size > 0) {
              setSelectedIds(new Set());
            } else {
              selectAllCalculated();
            }
          }}
        />
      ),
      cell: ({ row }: any) => {
        const assessment = row.original;
        // Only CALCULATED claims are selectable (approval auto-queues for payment)
        if (assessment.state !== 'CALCULATED') return null;
        if (Number(assessment.payoutAmount) === 0) {
          return (
            <div title="Payout is $0 because the sum insured for this peril has been fully paid">
              <Checkbox checked={false} disabled />
            </div>
          );
        }
        
        return (
          <Checkbox
            checked={selectedIds.has(assessment.id)}
            onCheckedChange={() => toggleSelection(assessment.id)}
          />
        );
      },
      size: 40,
    },
    {
      accessorKey: 'farmerName',
      header: 'Farmer',
      cell: ({ row }: any) => (
        <div>
          <div className="font-medium">{row.original.farmerName}</div>
          <div className="text-xs text-base-content/50">{row.original.farmerCode}</div>
        </div>
      ),
    },
    {
      accessorKey: 'peril',
      header: 'Peril',
    },
    {
      accessorKey: 'sumInsured',
      header: 'Sum Insured',
      cell: ({ getValue }: any) => {
        const value = getValue();
        return value ? `$${Number(value).toLocaleString()}` : '-';
      },
    },
    {
      accessorKey: 'payoutAmount',
      header: 'Payout Amount',
      cell: ({ getValue }: any) => {
        const value = Number(getValue());
        if (value === 0) {
          return (
            <span className="font-semibold text-base-content/50" title="Payout is $0 because the sum insured for this peril has been fully paid">
              $0.00
            </span>
          );
        }
        return <span className="font-semibold text-success">${value.toLocaleString()}</span>;
      },
    },
    {
      accessorKey: 'reading',
      header: 'Reading (mm)',
      cell: ({ getValue }: any) => {
        const value = getValue();
        return value !== null ? Number(value).toFixed(1) : '-';
      },
    },
    {
      accessorKey: 'eventDate',
      header: 'Event Date',
      cell: ({ getValue }: any) => getValue() || '-',
    },
    {
      accessorKey: 'state',
      header: 'State',
      cell: ({ getValue }: any) => {
        const state = getValue();
        return <Badge variant={getStateColor(state)}>{state}</Badge>;
      },
    },
    {
      id: 'mode',
      header: 'Mode',
      cell: ({ row }: any) => {
        const a = row.original;
        if (a.mock) return <Badge variant="outline">Mock</Badge>;
        const raw = a.rawResponse as Record<string, unknown> | null;
        if (raw?.test_mode) return <Badge variant="outline" className="border-orange-400 text-orange-700">Test</Badge>;
        return null;
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }: any) => {
        const assessment = row.original;
        const hasReport = assessment.reportData || assessment.rawResponse;
        const isExpanded = expandedRows.has(assessment.id);
        
        const handleDownload = () => {
          const reportData = {
            assessment: {
              id: assessment.id,
              participantId: assessment.participantId,
              runNumber: assessment.runNumber,
              peril: assessment.peril,
              payoutAmount: assessment.payoutAmount,
              reading: assessment.reading,
              eventDate: assessment.eventDate,
              reviewDate: assessment.reviewDate,
              state: assessment.state,
              mock: assessment.mock,
              reportDetail: assessment.reportDetail,
            },
            reportData: assessment.reportData,
            rawResponse: assessment.rawResponse,
          };
          
          const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `claims-assessment-${assessment.id}-${new Date().toISOString().split('T')[0]}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast.success('Report downloaded');
        };
        
        const toggleSummary = () => {
          const newExpanded = new Set(expandedRows);
          if (newExpanded.has(assessment.id)) {
            newExpanded.delete(assessment.id);
          } else {
            newExpanded.add(assessment.id);
          }
          setExpandedRows(newExpanded);
        };
        
        if (!hasReport) return null;
        
        return (
          <div className="flex items-center gap-2">
            {assessment.reportDetail === 'summary' || assessment.reportDetail === 'full' ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSummary}
                title={isExpanded ? 'Hide summary' : 'Show summary'}
              >
                {isExpanded ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              title="Download full report"
            >
              <DownloadIcon size={16} />
            </Button>
          </div>
        );
      },
      size: 100,
    },
  ];
  /* eslint-enable @typescript-eslint/no-explicit-any */

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/claims')}
            className="flex items-center gap-2"
          >
            <ChevronLeftIcon size={20} />
            Back to Claims
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-base-content flex items-center gap-2">
              <FileTextIcon size={24} />
              Policy {quote.quoteNumber}
            </h1>
            <p className="text-base-content/70 mt-1">
              {quote.product?.name || 'Policy Details'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant="default">
            Active Policy
          </Badge>
          {hasPermission('claims:create') && (
            <PositiveButton
              onClick={() => setShowCheckClaimsModal(true)}
              showIcon={false}
            >
              <PlayIcon size={16} />
              Check Claims
            </PositiveButton>
          )}
        </div>
      </div>

      {/* Policy Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileTextIcon size={18} />
              Policy Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-base-content/70">Policy Number</dt>
                <dd className="font-medium">{quote.quoteNumber}</dd>
              </div>
              <div>
                <dt className="text-sm text-base-content/70">Product</dt>
                <dd className="font-medium">{quote.product?.name}</dd>
              </div>
              <div>
                <dt className="text-sm text-base-content/70">Organization</dt>
                <dd className="font-medium">{quote.organization?.name}</dd>
              </div>
              <div>
                <dt className="text-sm text-base-content/70">Sum Insured</dt>
                <dd className="font-medium">${quote.sumInsured?.toLocaleString()}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon size={18} />
              Coverage Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-base-content/70">Coverage Start</dt>
                <dd className="font-medium">
                  {new Date(quote.coverageStartDate).toLocaleDateString()}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-base-content/70">Coverage End</dt>
                <dd className="font-medium">
                  {new Date(quote.coverageEndDate).toLocaleDateString()}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-base-content/70">Perils</dt>
                <dd className="font-medium">
                  {(quote.perilsConfig?.data?.perils ?? quote.perilsConfig?.perils)?.map(p => p.peril_id).join(', ') || '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-base-content/70">Participants</dt>
                <dd className="font-medium">{participants.length}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* Participants & Claims Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <CardTitle className="flex items-center gap-2">
                <DollarSignIcon size={18} />
                Claims Assessments
              </CardTitle>
              {currentJobId && (
                <Badge variant="secondary" className="flex items-center gap-2">
                  <span className="loading loading-spinner loading-xs"></span>
                  {jobStatus === 'queued' && 'Job queued...'}
                  {jobStatus === 'running' && 'Processing...'}
                  {jobStatus && !['queued', 'running'].includes(jobStatus) && `Status: ${jobStatus}`}
                </Badge>
              )}
            </div>
            {selectedIds.size > 0 && hasPermission('claims:approve') && (
              <PositiveButton
                onClick={() => setShowApproveModal(true)}
                showIcon={false}
              >
                <CheckIcon size={16} />
                Approve Selected ({selectedIds.size})
              </PositiveButton>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {assessmentRows.length === 0 ? (
            <div className="text-center py-8 text-base-content/50">
              <AlertTriangleIcon size={48} className="mx-auto mb-4 opacity-50" />
              <p>No claims assessments yet.</p>
              <p className="text-sm">Click "Check Claims" to run an assessment.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <DataTable
                data={assessmentRows}
                columns={participantColumns}
                enableSorting={true}
              />
              {/* Expanded summary rows */}
              {Array.from(expandedRows).map(assessmentId => {
                const assessment = assessmentRows.find(r => r.id === assessmentId);
                if (!assessment || !assessment.reportData) return null;
                
                const reportData = assessment.reportData as {
                  detail_level?: string;
                  summary?: Record<string, unknown>;
                  full?: Record<string, unknown>;
                };
                
                return (
                  <Card key={assessmentId} className="mt-2">
                    <CardHeader>
                      <CardTitle className="text-sm">
                        Report Summary - {assessment.farmerName} ({assessment.peril})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {reportData.summary ? (
                        <div className="space-y-2 text-sm">
                          {Object.entries(reportData.summary).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="text-base-content/70 capitalize">
                                {key.replace(/_/g, ' ')}:
                              </span>
                              <span className="font-medium">
                                {typeof value === 'object' 
                                  ? JSON.stringify(value, null, 2)
                                  : String(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-base-content/70">No summary data available</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Check Claims Modal */}
      {showCheckClaimsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-base-100 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Check Claims</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowCheckClaimsModal(false)}>
                <XIcon size={20} />
              </Button>
            </div>
            <div className="space-y-4">
              <p className="text-base-content/70">
                This will trigger a claims assessment for all participants in this policy.
              </p>
              <Select
                label="Assessment Mode"
                value={assessmentMode}
                onValueChange={(v) => setAssessmentMode(v as 'production' | 'mock' | 'test')}
                options={[
                  { value: 'production', label: 'Production - Real CHIRPS data from R-Server' },
                  { value: 'mock', label: 'Mock - Generates mock payout records locally' },
                  { value: 'test', label: 'Test - Synthetic data with configurable triggers' },
                ]}
              />
              {assessmentMode === 'production' && (
                <div className="alert alert-warning">
                  <AlertTriangleIcon size={16} />
                  <span>Real R-Server calculations may take several minutes.</span>
                </div>
              )}
              {assessmentMode === 'test' && (
                <div className="space-y-3 p-4 bg-base-200 rounded-lg">
                  <div className="text-sm font-medium">Trigger Configuration</div>
                  {(data?.quote?.perilsConfig?.data?.perils ?? data?.quote?.perilsConfig?.perils ?? []).map(p => (
                    <Select
                      key={p.peril_id}
                      label={p.peril_id}
                      value={String(testTriggers[p.peril_id] ?? 'false')}
                      onValueChange={(v) => setTestTriggers(prev => ({
                        ...prev,
                        [p.peril_id]: v === 'false' ? false : v as 'strike' | 'exhaust',
                      }))}
                      options={[
                        { value: 'false', label: 'No Trigger' },
                        { value: 'strike', label: 'Strike - Partial payout' },
                        { value: 'exhaust', label: 'Exhaust - Maximum payout' },
                      ]}
                    />
                  ))}
                  <Select
                    label="Data Coverage Period"
                    value={dataCoveragePeriod}
                    onValueChange={(v) => setDataCoveragePeriod(v as 'whole' | 'germination' | 'part' | 'none')}
                    options={[
                      { value: 'whole', label: 'Whole - Full coverage period' },
                      { value: 'germination', label: 'Germination - Data up to Jan 15 only' },
                      { value: 'part', label: 'Part - First half of pentads only' },
                      { value: 'none', label: 'None - No data (empty data handling)' },
                    ]}
                  />
                </div>
              )}
              {assessmentMode !== 'mock' && (
                <>
                  <div className="space-y-2">
                    <Select
                      label="Report Detail Level"
                      value={reportDetail}
                      onValueChange={(value) => {
                        const detailLevel = value as 'minimal' | 'summary' | 'full';
                        setReportDetail(detailLevel);
                        if (detailLevel !== 'full') {
                          setIncludeRawData(false);
                        }
                      }}
                      options={[
                        { value: 'minimal', label: 'Minimal - Basic payout information only' },
                        { value: 'summary', label: 'Summary - Includes calculation summary and statistics' },
                        { value: 'full', label: 'Full - Complete report with all calculation details' },
                      ]}
                    />
                  </div>
                  {reportDetail === 'full' && (
                    <div className="flex items-center justify-between p-4 bg-base-200 rounded-lg">
                      <div>
                        <div className="font-medium">Include Raw CHIRPS Data</div>
                        <div className="text-sm text-base-content/70">
                          Include raw time series data in the report (large file size)
                        </div>
                      </div>
                      <Switch
                        checked={includeRawData}
                        onCheckedChange={setIncludeRawData}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <NeutralButton onClick={() => setShowCheckClaimsModal(false)}>
                Cancel
              </NeutralButton>
              <PositiveButton
                onClick={handleCheckClaims}
                disabled={assessing}
                showIcon={false}
              >
                {assessing ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Assessing...
                  </>
                ) : (
                  <>
                    <PlayIcon size={16} />
                    Execute
                  </>
                )}
              </PositiveButton>
            </div>
          </div>
        </div>
      )}

      {/* Approve Confirmation Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-base-100 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Confirm Approval</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowApproveModal(false)}>
                <XIcon size={20} />
              </Button>
            </div>
            <p className="text-base-content/70">
              Are you sure you want to approve {selectedIds.size} claims assessment(s)?
            </p>
            <p className="text-sm text-base-content/50 mt-2">
              Approved claims will be automatically queued for payment. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <NeutralButton onClick={() => setShowApproveModal(false)}>
                Cancel
              </NeutralButton>
              <PositiveButton
                onClick={handleApprove}
                disabled={approving}
                showIcon={false}
              >
                {approving ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Approving...
                  </>
                ) : (
                  <>
                    <CheckIcon size={16} />
                    Approve
                  </>
                )}
              </PositiveButton>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
